// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import React, { useCallback, useEffect, useState } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core";

import Loader from "components/ui/Loader";
import { TestRunParams } from "./TestRunConstants";
import Timer from "components/ui/Timer";
import { useParams } from "react-router-dom";
import { useTestRunConfig } from "./useTestRunConfig";
import { useTestRunState } from "store/testrun";

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    timedStepContent: {
      maxWidth: `300px`,
      margin: "auto",
      display: "block",
    },

    timedStepTimer: {
      marginTop: "1rem",
    },
  })
);

export interface interfaceTimedStepProps {
  duration: number;
  testRunUID: string;
  setStepReady: (ready: boolean) => void;
  description: string;
}

export default (props: interfaceTimedStepProps) => {
  const { duration, setStepReady, description } = props;
  const { step } = useParams<TestRunParams>();
  const classes = useStyle();
  const { getTestRunStep } = useTestRunConfig();
  const testRunState = useTestRunState();

  // Time is complete, enable the "next" button.
  const timerCompleteCallback = useCallback(() => {
    setStepReady && setStepReady(true);
  }, [setStepReady]);

  // store the timer start time, will be updated via fastforward click.
  const [startTimeInternal, setStartTimeInternal] = useState<number | null>(
    null
  );

  const stepName = getTestRunStep(step!).name;

  // Time when the step was first accessed.
  const registeredStartTime =
    step && testRunState.testRun?.steps
      ? testRunState.testRun?.steps[stepName]?.firstVisitedTime
      : undefined; // not loaded yet

  // Set the starttime once the value is loaded.
  useEffect(() => {
    if (!testRunState.subscribed) {
      return;
    }
    setStepReady && setStepReady(false);

    if (registeredStartTime !== undefined) {
      setStartTimeInternal(registeredStartTime);
    }
  }, [registeredStartTime, setStepReady, testRunState.subscribed]);

  // fast forward on click.
  const onClickWrapper = useCallback(() => {
    if (!testRunState.subscribed || startTimeInternal === null) {
      return;
    }
    const now = Date.now();
    if (duration - (now - startTimeInternal) > 5000) {
      setStartTimeInternal(now - duration + 5000);
    }
  }, [testRunState.subscribed, startTimeInternal, duration]);

  const showLoader = !testRunState.subscribed || startTimeInternal === null;

  return (
    <div onClick={onClickWrapper}>
      {description && <div>{props.description}</div>}
      <div className={classes.timedStepContent}>
        {showLoader && <Loader />}
        {!showLoader && (
          <div className={classes.timedStepTimer}>
            <Timer
              duration={duration}
              startTime={startTimeInternal!}
              onTimerComplete={timerCompleteCallback}
            />
          </div>
        )}
      </div>
    </div>
  );
};
